import type { MissionProgress as MissionProgressType } from "@/entities/missions";

type Props = {
  progress: MissionProgressType;
  /** Explicit id root for tests + a11y. */
  testIdRoot?: string;
};

/**
 * Deterministic progress renderer.
 *
 * Renders a bar + "N of M · step-name" label. Announces progress to
 * screen readers via ``role=progressbar`` with aria-valuenow.
 */
export function MissionProgress({ progress, testIdRoot = "mission-shell" }: Props) {
  const { current, total, step_key } = progress;
  const pct = total > 0 ? Math.min(100, Math.max(0, Math.round((current / total) * 100))) : 0;
  return (
    <div
      className="flex items-center gap-3"
      data-testid={`${testIdRoot}-progress`}
    >
      <div
        className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden"
        role="progressbar"
        aria-valuenow={current}
        aria-valuemin={0}
        aria-valuemax={total || 100}
        aria-label={
          total > 0
            ? `Step ${current} of ${total}${step_key ? ` · ${step_key}` : ""}`
            : step_key || "Progress"
        }
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-violet-500 transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div
        className="text-xs font-medium text-white/70 tabular-nums"
        data-testid={`${testIdRoot}-progress-label`}
      >
        {total > 0 ? `${current} of ${total}` : step_key || "—"}
      </div>
    </div>
  );
}
